import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Fab, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from 'src/stores';
import { currentRole, isLivyAdmin, signedInSubject, useLivyAccess } from './access';
import { livyConfig } from 'src/lib/livyTransport';
import { LivyDrawer } from './LivyDrawer';
import { LivyAvatar } from './LivyAvatar';
import { LivyCommandPalette, useCommandPalette } from './LivyCommandPalette';
import { ASK_EVENT, HELP_EVENT, LivyGreeting, greetingActions, greetingSeen, markGreetingSeen, livyEvent, resumePrompt, useEngagement } from './LivyGreeting';
import { useBillingType } from './billingType';

/**
 * The palette dispatches its ask while its dialog is still open, and the dialog's focus trap and
 * focus restore would take the cursor straight back, so this waits for every dialog to unmount.
 */
function focusPageComposer() {
  var _document$querySelect;
  let tries = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 30;
  if (document.querySelector('.MuiDialog-root')) {
    // A dialog that stays open keeps the cursor, so after about 1.5s this gives up.
    if (tries > 0) setTimeout(() => focusPageComposer(tries - 1), 50);
    return;
  }
  (_document$querySelect = document.querySelector('[aria-label="Message Livy"]')) === null || _document$querySelect === void 0 || _document$querySelect.focus();
}

/**
 * Mounts everything that should exist on every authenticated page: the support button that opens
 * the drawer, Cmd/Ctrl-I to toggle it, Cmd-K, and the login greeting. Staff always; customers once
 * staff switch their client on. Search is fenced server side to what each caller may read.
 */
const LivyLauncherBody = observer(function LivyLauncherBody() {
  var _store$homeDashboardS;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  // The full Livy page is already the chat: no launcher, shortcut or greeting opens a second one over
  // it, a drawer left open elsewhere closes on arrival, and asks go into the page's own chat. Help
  // requests still open the drawer, which is where the help articles live.
  // Matched like the router matches it: case-insensitive, trailing slash allowed.
  const navigate = useNavigate();
  const _useLocation = useLocation(),
    pathname = _useLocation.pathname;
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    pendingAsk = _useState4[0],
    setPendingAsk = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    pendingHelp = _useState6[0],
    setPendingHelp = _useState6[1];
  // Per login, from session storage: a second sign-in without a reload is a new greeting.
  const _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    rerender = _useState8[1];
  const seen = greetingSeen();
  const store = useStore();
  const enabled = useLivyAccess(store.account.userProfile.userRole);
  // Only where a full page actually serves this person: /livy is staff's page (and the customer page for
  // anyone else with Livy), /livy/chat is everyone's.
  const onLivyPage = /^\/livy(\/chat)?\/?$/i.test(pathname) && Boolean(livyConfig()) && (isLivyAdmin(currentRole()) || enabled);
  useEffect(() => {
    if (onLivyPage) setOpen(false);
  }, [onLivyPage]);
  const _useCommandPalette = useCommandPalette(enabled),
    paletteOpen = _useCommandPalette.open,
    setPaletteOpen = _useCommandPalette.setOpen;
  const engagement = useEngagement(enabled);
  const billingType = useBillingType();
  // The dashboard store already holds every property's status for the navigation, so this costs no request.
  const inSetup = Boolean((_store$homeDashboardS = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS === void 0 || (_store$homeDashboardS = _store$homeDashboardS.portfolios) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.some(p => p.properties.some(x => x.propertyStatus === 'Inactive')));
  const dismiss = useCallback(() => {
    markGreetingSeen();
    rerender(n => n + 1);
  }, []);
  // Closing the greeting unasked counts as a dismissed tip; two of them and tips stop early.
  const dismissTip = useCallback(() => {
    void livyEvent('tip_dismissed');
    dismiss();
  }, [dismiss]);
  const openWith = useCallback(text => {
    dismiss();
    setOpen(true);
    // An ask, even an empty one, opens on Chat; a bare open keeps the last tab.
    if (text !== undefined) setPendingAsk(text);
  }, [dismiss]);
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = e => {
      if (onLivyPage) return;
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'i') {
        e.preventDefault();
        setOpen(v => !v);
      }
    };
    const onAsk = e => {
      const text = e.detail;
      if (!onLivyPage) openWith(text);
      // An empty ask is "open the chat", and on this page the chat is already open: put the cursor in it.
      else if (!text) focusPageComposer();
      // Replaces, so Back leaves the page; a second ask before the page sends the first one wins.
      // Router state, not ?ask=: see usePageAsk.
      else navigate(pathname, {
        replace: true,
        state: {
          livyAsk: text
        }
      });
    };
    const onHelp = e => {
      openWith();
      setPendingHelp(e.detail);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(ASK_EVENT, onAsk);
    window.addEventListener(HELP_EVENT, onHelp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(ASK_EVENT, onAsk);
      window.removeEventListener(HELP_EVENT, onHelp);
    };
  }, [enabled, openWith, onLivyPage, navigate, pathname]);
  if (!enabled) return null;
  const greeting = Boolean(engagement === null || engagement === void 0 ? void 0 : engagement.greet) && !seen && !open && !onLivyPage;
  // After the tip window Livy waits to be asked, unless there is setup left to finish.
  const badge = !open && !seen && (greeting || Boolean(engagement === null || engagement === void 0 ? void 0 : engagement.enabled) && !(engagement !== null && engagement !== void 0 && engagement.tips) && inSetup);
  return /*#__PURE__*/React.createElement(React.Fragment, null, !onLivyPage && /*#__PURE__*/React.createElement(Tooltip, {
    title: "Livy (\u2318I / Ctrl+I)",
    placement: "left"
  }, /*#__PURE__*/React.createElement(Fab, {
    color: "primary",
    size: "medium",
    "aria-label": badge ? 'Ask Livy, new suggestions' : 'Ask Livy',
    onClick: () => openWith(),
    sx: {
      position: 'fixed',
      // Bottom-right corner; HubSpot's chat launcher owns the bottom 100x96 when it loads, so
      // Livy stacks above it then. Re-read on every navigation, which re-renders this.
      right: 24,
      bottom: document.getElementById('hubspot-messages-iframe-container') ? 104 : 24,
      zIndex: theme => theme.zIndex.drawer - 1,
      p: 0,
      overflow: 'visible'
    }
  }, /*#__PURE__*/React.createElement(Badge, {
    color: "error",
    variant: "dot",
    invisible: !badge,
    overlap: "circular",
    sx: {
      '& .MuiBadge-dot': {
        width: 12,
        height: 12,
        borderRadius: '6px',
        border: '2px solid #fff'
      }
    }
  }, /*#__PURE__*/React.createElement(LivyAvatar, {
    size: 48
  })))), greeting && /*#__PURE__*/React.createElement(LivyGreeting, {
    actions: greetingActions(billingType(), inSetup, engagement === null || engagement === void 0 ? void 0 : engagement.favorite),
    resume: resumePrompt(engagement === null || engagement === void 0 ? void 0 : engagement.usualRange),
    onAsk: openWith,
    onDismiss: dismissTip
  }), /*#__PURE__*/React.createElement(LivyDrawer, {
    open: open,
    onClose: () => setOpen(false),
    pendingAsk: pendingAsk,
    onAsked: () => setPendingAsk(null),
    pendingHelp: pendingHelp,
    onHelped: () => setPendingHelp(null)
  }), enabled && /*#__PURE__*/React.createElement(LivyCommandPalette, {
    open: paletteOpen,
    onClose: () => setPaletteOpen(false)
  }));
});
export function LivyLauncher() {
  // Logout and the next login happen without a reload, and both navigate. Re-rendering on the
  // location and keying on the session remounts every Livy surface empty for the next person, so a
  // drawer or palette left open never shows them the last person's conversation or results.
  useLocation();
  return /*#__PURE__*/React.createElement(LivyLauncherBody, {
    key: signedInSubject()
  });
}